// 사운드 디자인 (§17): 미니멀·건조·촉각적. 배경음악·팡파레 없음.
// 시그니처 둘 — 종이 크런치(풀어주는 소리) + 도장 쾅(판정). Web Audio 합성(에셋 무의존).
// 토글: 설정 소리 on/off. 기본 on(크런치가 핵심 경험).

const KEY = "bwek_sound";

export function soundEnabled(): boolean {
  return localStorage.getItem(KEY) !== "off";
}
export function setSoundEnabled(on: boolean): void {
  localStorage.setItem(KEY, on ? "on" : "off");
}

let ctx: AudioContext | null = null;
function ac(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!ctx) ctx = new Ctor();
  if (ctx.state === "suspended") void ctx.resume();
  return ctx;
}

/** 사용자 제스처(예: 제출 탭) 때 오디오 컨텍스트를 미리 깨워둔다 → 직후 재생 지연 제거. */
export function warmupSound(): void {
  ac();
}

// ── 구겨버리기 소리: 영상(crumple.mp4)에서 추출한 실제 오디오(구김+튕김)를 Web Audio로 재생 ──
// 영상은 음소거 자동재생(재생버튼 방지)하고, 소리는 이 버퍼로 동기화 재생. unmute 제스처 이슈 없음.
let crumpleBuffer: AudioBuffer | null = null;
let crumpleLoading: Promise<AudioBuffer | null> | null = null;
function loadCrumple(c: AudioContext): Promise<AudioBuffer | null> {
  if (crumpleBuffer) return Promise.resolve(crumpleBuffer);
  if (!crumpleLoading) {
    crumpleLoading = fetch("/assets/crumple.mp3")
      .then((r) => r.arrayBuffer())
      .then((b) => c.decodeAudioData(b))
      .then((buf) => (crumpleBuffer = buf))
      .catch(() => null);
  }
  return crumpleLoading;
}

/** 구겨버리기 화면 진입 시 미리 디코딩(재생 지연 방지). */
export function preloadCrumple(): void {
  const c = ac();
  if (c) void loadCrumple(c);
}

/** 추출한 구김+튕김 소리 재생. 영상 재생 시작(playing) 시점에 호출 → 동기화. */
export function playCrumpleSound(): void {
  if (!soundEnabled()) return;
  const c = ac();
  if (!c) return;
  void loadCrumple(c).then((buf) => {
    if (!buf) return;
    const src = c.createBufferSource();
    src.buffer = buf;
    src.connect(c.destination);
    src.start();
  });
}

function noiseBuffer(c: AudioContext, sec: number): AudioBuffer {
  const len = Math.floor(c.sampleRate * sec);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

/** 종이 크런치: 촘촘한 무작위 크래클(브로드밴드) + 낮은 러슬 바디. 영상 구김 동작에 맞춘 자연음. */
export function playCrunch(): void {
  if (!soundEnabled()) return;
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime;
  const dur = 0.85; // 2배속 영상의 구김 비트에 맞춤

  // 레이어 1 — 밝은 크래클: 연속 노이즈에 촘촘한 무작위 게인 스텝(딱딱 끊기는 종이 결).
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, dur + 0.1);
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 2600;
  bp.Q.value = 0.5;
  const hp = c.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 900;
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  let t = t0 + 0.008;
  while (t < t0 + dur) {
    const frac = (t - t0) / dur;
    const ceiling = 0.24 * Math.pow(1 - frac, 1.3) + 0.015; // 빠르게 일었다 잦아듦
    const amp = ceiling * (0.2 + Math.random() * Math.random() * 1.1); // 불규칙 크래클
    g.gain.setValueAtTime(Math.max(0.001, amp), t);
    t += 0.009 + Math.random() * 0.016; // 9~25ms 간격
  }
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur + 0.08);
  src.connect(bp).connect(hp).connect(g).connect(c.destination);
  src.start(t0);
  src.stop(t0 + dur + 0.12);

  // 레이어 2 — 낮은 러슬 바디(종이 부피감).
  const src2 = c.createBufferSource();
  src2.buffer = noiseBuffer(c, dur);
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 850;
  const g2 = c.createGain();
  g2.gain.setValueAtTime(0.0001, t0);
  g2.gain.exponentialRampToValueAtTime(0.07, t0 + 0.05);
  g2.gain.exponentialRampToValueAtTime(0.0001, t0 + dur * 0.85);
  src2.connect(lp).connect(g2).connect(c.destination);
  src2.start(t0);
  src2.stop(t0 + dur);
}

/** 도장 쾅: 낮은 thud(삼각파) + 짧은 노이즈 클릭. 환호 아님, 딱 찍고 끝. */
export function playStamp(): void {
  if (!soundEnabled()) return;
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime;
  // thud
  const osc = c.createOscillator();
  osc.type = "triangle";
  osc.frequency.setValueAtTime(180, t0);
  osc.frequency.exponentialRampToValueAtTime(60, t0 + 0.12);
  const og = c.createGain();
  og.gain.setValueAtTime(0.0001, t0);
  og.gain.exponentialRampToValueAtTime(0.35, t0 + 0.006);
  og.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.22);
  osc.connect(og).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.24);
  // click
  const src = c.createBufferSource();
  src.buffer = noiseBuffer(c, 0.03);
  const hp = c.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 2000;
  const g = c.createGain();
  g.gain.setValueAtTime(0.25, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.05);
  src.connect(hp).connect(g).connect(c.destination);
  src.start(t0);
  src.stop(t0 + 0.05);
}

/** 마이크 켜고 끌 때 작은 칩. */
export function playChirp(up = true): void {
  if (!soundEnabled()) return;
  const c = ac();
  if (!c) return;
  const t0 = c.currentTime;
  const osc = c.createOscillator();
  osc.type = "sine";
  osc.frequency.setValueAtTime(up ? 620 : 480, t0);
  osc.frequency.exponentialRampToValueAtTime(up ? 920 : 360, t0 + 0.08);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.12, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
  osc.connect(g).connect(c.destination);
  osc.start(t0);
  osc.stop(t0 + 0.13);
}
